import type { CredentialState, TrustTier, TrustTierMeta } from './types'
import { PLANS } from '../../api/lib/plans.mjs'

// ── Trust ladder ───────────────────────────────────────────────────────────
// Each rung is additive. The UI always states what a rung proves — and the
// profile spells out what each signal does and does not establish.
export const TRUST_TIERS: Record<TrustTier, TrustTierMeta> = {
  1: {
    tier: 1,
    name: 'Real Human',
    blurb: 'A live person passed device + liveness checks. Not a bot, not a duplicate account.',
  },
  2: {
    tier: 2,
    name: 'Verified Professional',
    blurb: 'Identity, contact, and professional presence corroborated across independent sources.',
  },
  3: {
    tier: 3,
    name: 'Deeply Verified',
    blurb: 'Employment, credentials, and a real-time Live Vouch confirm this person end to end.',
  },
}

// Signals that *corroborate* identity but do not gate tier advancement — they add
// confidence (a cross-platform name match) without making a tier harder to reach.
// LinkedIn is corroborating: a verified GitHub already earns the Tier-2 rung, and
// LinkedIn reinforces the same identity rather than adding a new requirement.
// `location_consistency` is corroborating too — the device/profile/phone locations
// lining up is a confidence boost, never a gate (location is maskable: VPN, travel,
// "Remote"). `web_custody` is corroborating as well — it marks that the holder key
// is a web passkey (it informs the Tier-2 cap in tierMet, it never gates a rung).
// Mirrors iOS `SignalCatalog.corroboratingIds` and the api recomputeTier set.
export const CORROBORATING_SIGNAL_IDS = new Set<string>(['linkedin', 'location_consistency', 'web_custody'])

// Signals listed for display but PAUSED from tier gating — they can't be issued
// anywhere right now (gov_id/Persona + employment/Truework are on legal hold, and
// iOS already dropped them from SignalCatalog.all). Excluding them from the rung
// keeps the per-rung "Met" badge honest: leaving them in made Tier 2 impossible to
// complete (gov_id never present), so Tier 3 never lit while iOS showed it (H13).
// Mirrors api/lib/tier.mjs PAUSED_SIGNAL_IDS; iOS achieves the same by omission.
export const PAUSED_SIGNAL_IDS = new Set<string>(['gov_id', 'employment'])

// ── Credential-state presentation ────────────────────────────────────────────
export const CRED_META: Record<
  CredentialState,
  { label: string; color: string; bg: string; live?: boolean }
> = {
  live: { label: 'Live verified', color: 'var(--cred-live)', bg: 'var(--cred-live-bg)', live: true },
  founding: { label: 'Founding cohort', color: 'var(--cred-founding)', bg: 'var(--cred-founding-bg)' },
  vault: { label: 'Vault verified', color: 'var(--cred-vault)', bg: 'var(--cred-vault-bg)' },
  passive: { label: 'Passive signals', color: 'var(--cred-passive)', bg: 'var(--cred-passive-bg)' },
  stale: { label: 'Verification aged', color: 'var(--cred-stale)', bg: 'var(--cred-stale-bg)' },
}

// ── Plans / pricing ──────────────────────────────────────────────────────────
// The plan list is defined ONCE in api/lib/plans.mjs (plain JS, no build step)
// so three consumers share one definition: this SPA, the API (billing webhook +
// the public /api/plans route), and the marketing site (which proxies that
// route). The Vite build does `COPY . . && npm run build`, so importing across
// the api/ boundary resolves and bundles cleanly. Edit pricing THERE, not here.
export { PLANS }

export const planById = (id: string) => PLANS.find((p) => p.id === id)!

// Personal email domains are rejected at registration — recruiters must use a
// corporate domain. This is a real product rule, surfaced inline in the UI.
export const PERSONAL_EMAIL_DOMAINS = new Set([
  'gmail.com',
  'googlemail.com',
  'outlook.com',
  'hotmail.com',
  'live.com',
  'msn.com',
  'yahoo.com',
  'ymail.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'proton.me',
  'protonmail.com',
  'gmx.com',
  'zoho.com',
  'pm.me',
  'mail.com',
  'yandex.com',
  'duck.com',
])

export const RECRUITER_ROLES = [
  'Technical Recruiter',
  'Talent Partner',
  'Head of Talent',
  'Hiring Manager',
  'Sourcer',
  'People Ops',
  'Founder / Exec',
]
